import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import api, { formatINR } from "../../lib/api";
import { PAYMENT_LABEL, STATUS_LABEL, STATUS_STYLE, errorMessage, fmtDay, newIdempotencyKey } from "../../lib/stays";

const STATUSES = ["pending", "confirmed", "checked_in", "completed", "cancelled", "no_show", "expired"];
const ACTIONS = {
  pending: [["confirmed", "Confirm"], ["cancelled", "Cancel"]],
  confirmed: [["checked_in", "Check in"], ["no_show", "No-show"], ["cancelled", "Cancel"]],
  checked_in: [["completed", "Check out"]],
};

const Badge = ({ status }) => (
  <span className={`text-[10px] uppercase tracking-[0.18em] border px-2 py-1 ${STATUS_STYLE[status] || ""}`}>{STATUS_LABEL[status] || status}</span>
);

function Json({ v }) {
  if (v == null) return <span className="text-white/30">—</span>;
  const { pricing_snapshot, cancellation, ...rest } = v;
  return (
    <pre className="text-[11px] text-white/60 whitespace-pre-wrap break-words">
      {JSON.stringify(rest, null, 1).replace(/[{}",]/g, "").replace(/\n\s*\n/g, "\n").trim()}
      {cancellation ? `\nrefund ${cancellation.refund_percent}% → ${cancellation.refund_amount}` : ""}
    </pre>
  );
}

function BookingDetail({ id, onClose, onChanged }) {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "cash" });

  const load = useCallback(async () => {
    try { setB((await api.get(`/admin/bookings/${id}`)).data); } catch (e) { toast.error(errorMessage(e)); onClose(); }
  }, [id, onClose]);
  useEffect(() => { load(); }, [load]);

  const act = async (status) => {
    let body = { status };
    if (status === "cancelled") {
      const reason = window.prompt("Reason for cancelling (optional):", "");
      if (reason === null) return;
      const pct = window.prompt(`Refund % to apply (leave empty to follow the booking's ${b.refund_type === "NON_REFUNDABLE" ? "non-refundable" : "cancellation"} policy):`, "");
      if (pct === null) return;
      body = { status, reason, ...(pct !== "" ? { refund_percent: Number(pct) } : {}) };
    } else if (!window.confirm(`Mark this booking as ${STATUS_LABEL[status]}?`)) return;
    setBusy(true);
    try { setB((await api.patch(`/admin/bookings/${id}/status`, body)).data); toast.success("Booking updated"); onChanged(); await load(); }
    catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };

  const confirmPayment = async () => {
    if (!window.confirm(`Confirm this booking and record ${formatINR(b.total_amount - b.amount_paid)} as received?`)) return;
    setBusy(true);
    try {
      setB((await api.post(`/admin/bookings/${id}/confirm-payment`, { method: "upi_qr" })).data);
      toast.success("Payment recorded and booking confirmed");
      onChanged();
      await load();
    } catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };

  const record = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${id}/payments`, { kind, amount: pay.amount, method: pay.method });
      toast.success(kind === "refund" ? "Refund recorded" : "Payment recorded");
      setPay({ amount: "", method: "cash" }); onChanged(); await load();
    } catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0b0a] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto text-white" data-testid="admin-booking-detail">
        <DialogHeader><DialogTitle className="font-display text-2xl">{b ? b.reference : "Loading…"}</DialogTitle></DialogHeader>
        {!b ? <div className="py-10 text-center text-white/40">Loading…</div> : (
          <div className="space-y-6 text-sm">
            <div className="flex flex-wrap gap-2 items-center"><Badge status={b.status} />
              <span className="text-white/50">{PAYMENT_LABEL[b.payment_status]}</span>
              <span className="text-white/30">· {b.source}</span>
              {b.refund_type === "NON_REFUNDABLE" && <span className="text-red-300 border border-red-500/30 px-2 py-1 text-xs">Non-refundable</span>}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div><div className="text-white/40 text-xs uppercase">Guest</div>{b.guest_name}<div className="text-white/50">{b.guest_email} · {b.guest_phone}</div></div>
              <div><div className="text-white/40 text-xs uppercase">Stay</div>{fmtDay(b.check_in_date)} → {fmtDay(b.check_out_date)}
                <div className="text-white/50">{b.check_in_time_label} → {b.check_out_time_label} · {b.booking_type === "overnight" ? `${b.number_of_nights} nights` : "hourly"}</div></div>
              <div><div className="text-white/40 text-xs uppercase">Guests</div>{b.adults} adults, {b.children} children, {b.infants} infants, {b.pets} pets</div>
              <div><div className="text-white/40 text-xs uppercase">Unit</div>{b.unit} · {b.property.name}</div>
            </div>

            <div>
              <div className="text-white/40 text-xs uppercase mb-2">Pricing breakdown</div>
              <div className="space-y-1">{b.pricing?.line_items?.map((l) => <div key={l.key} className="flex justify-between"><span className="text-white/60">{l.label}</span><span>{l.amount}</span></div>)}</div>
              <div className="flex justify-between font-display text-lg border-t border-white/10 mt-2 pt-2"><span>Total</span><span className="text-gold">{formatINR(b.total_amount)}</span></div>
              <div className="text-white/50 text-xs mt-1">Paid {formatINR(b.amount_paid)} · refunded {formatINR(b.refund_amount)} · refund due {formatINR(b.refund_due)}</div>
            </div>

            {b.transactions?.length > 0 && (
              <div data-testid="admin-transactions">
                <div className="text-white/40 text-xs uppercase mb-2">Payments and refunds</div>
                <div className="space-y-1">
                  {b.transactions.map((t) => (
                    <div key={t.id} className="flex flex-wrap justify-between gap-2 text-white/70">
                      <span>{t.kind} · {t.provider}{t.method && t.method !== t.provider ? ` (${t.method.replace(/_/g, " ")})` : ""} · {t.status}</span>
                      <span>{t.kind === "refund" ? "-" : ""}{formatINR(t.amount)} <span className="text-white/30 text-xs">{t.reference}</span></span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-white/40 text-xs uppercase mb-2">Policy acceptance</div>
              <div className="grid grid-cols-2 gap-1 text-white/70">
                <span>Rules: {b.accepted.rules ? "✓" : "—"}</span><span>Cancellation policy: {b.accepted.cancellation_policy ? "✓" : "—"}</span>
                <span>Timings: {b.accepted.timings ? "✓" : "—"}</span><span>Non-refundable: {b.accepted.non_refundable ? "✓ accepted" : "—"}</span>
              </div>
              {b.accepted.accepted_at && <div className="text-white/40 text-xs mt-1">Accepted {new Date(b.accepted.accepted_at).toLocaleString("en-IN")}</div>}
            </div>

            {(b.status === "pending" || b.status === "expired") && Number(b.amount_paid) < Number(b.total_amount) && (
              <div className="border border-gold/40 bg-gold/[0.05] p-4" data-testid="confirm-payment-box">
                <div className="text-white">Guest sent a payment screenshot?</div>
                <p className="text-xs text-white/50 mt-1">Check the amount ({formatINR(b.total_amount)}) and UPI reference, then confirm. The dates are re-checked{b.status === "expired" ? " (the hold had expired)" : ""}.</p>
                <button disabled={busy} onClick={confirmPayment} className="btn-gold !py-2 !px-4 mt-3 disabled:opacity-40" data-testid="confirm-payment">Payment received - confirm booking</button>
              </div>
            )}

            {(ACTIONS[b.status] || []).length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="admin-actions">
                {ACTIONS[b.status].map(([s, label]) => (
                  <button key={s} disabled={busy} onClick={() => act(s)} className={`px-4 py-2 border text-xs uppercase tracking-[0.15em] disabled:opacity-40 ${s === "cancelled" || s === "no_show" ? "border-red-500/40 text-red-300" : "border-gold/40 text-gold"}`}>{label}</button>
                ))}
              </div>
            )}

            {!["cancelled", "expired"].includes(b.status) && (
              <div className="border border-white/10 p-4">
                <div className="text-white/40 text-xs uppercase mb-2">Record payment received at property</div>
                <div className="flex flex-wrap gap-2">
                  <Input type="number" min="0" placeholder="Amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="bg-[#0f0f0f] border-white/10 h-10 w-32" aria-label="Payment amount" />
                  <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className="bg-[#0f0f0f] border border-white/10 h-10 px-2" aria-label="Payment method">
                    {["cash", "upi", "card_at_property", "bank_transfer"].map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
                  </select>
                  <button disabled={busy || !pay.amount} onClick={() => record("payment")} className="btn-gold !py-2 !px-4 disabled:opacity-40">Record payment</button>
                  <button disabled={busy || !pay.amount} onClick={() => record("refund")} className="btn-ghost !py-2 !px-4 disabled:opacity-40">Record refund</button>
                </div>
              </div>
            )}

            <div>
              <div className="text-white/40 text-xs uppercase mb-2">Activity history</div>
              <ol className="space-y-3 border-l border-white/10 pl-4" data-testid="admin-events">
                {b.events.map((e) => (
                  <li key={e.id}>
                    <div className="text-white">{e.action.replace(/_/g, " ")} <span className="text-white/40">· {e.actor} · {new Date(e.created_at).toLocaleString("en-IN")}</span></div>
                    {e.note && <div className="text-white/50 text-xs">{e.note}</div>}
                    {(e.previous_value || e.new_value) && (
                      <details className="text-xs text-white/40"><summary className="cursor-pointer">changes</summary>
                        <div className="grid sm:grid-cols-2 gap-3 mt-1"><div><b>Before</b><Json v={e.previous_value} /></div><div><b>After</b><Json v={e.new_value} /></div></div>
                      </details>)}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ManualBooking({ property, onClose, onCreated }) {
  const [f, setF] = useState({ booking_type: "overnight", check_in_date: "", check_out_date: "", date: "", start_time: "19:00", hours: 3, adults: 2, children: 0, guest_name: "", guest_email: "", guest_phone: "", special_requests: "", refund_type: property?.cancellation?.refund_type || "REFUNDABLE" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const keyRef = useState(() => newIdempotencyKey())[0];
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/bookings", { ...f, property_id: property.id }, { headers: { "Idempotency-Key": keyRef } });
      toast.success(`Booking ${data.reference} created`); onCreated(); onClose();
    } catch (e) { setErr(errorMessage(e)); }
    setBusy(false);
  };
  const inp = "mt-1 bg-[#0f0f0f] border-white/10 h-10";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0b0a] border-white/10 max-w-xl max-h-[90vh] overflow-y-auto text-white" data-testid="manual-booking-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Create booking</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><Label>Type</Label>
            <select value={f.booking_type} onChange={set("booking_type")} className="mt-1 w-full bg-[#0f0f0f] border border-white/10 h-10 px-2"><option value="overnight">Overnight</option><option value="hourly">Hourly</option></select></div>
          {f.booking_type === "overnight" ? (<>
            <div><Label>Check-in</Label><Input type="date" value={f.check_in_date} onChange={set("check_in_date")} className={inp} /></div>
            <div><Label>Check-out</Label><Input type="date" value={f.check_out_date} onChange={set("check_out_date")} className={inp} /></div>
          </>) : (<>
            <div><Label>Date</Label><Input type="date" value={f.date} onChange={set("date")} className={inp} /></div>
            <div><Label>Start</Label><Input type="time" value={f.start_time} onChange={set("start_time")} className={inp} /></div>
            <div><Label>Hours</Label><Input type="number" value={f.hours} onChange={set("hours")} className={inp} /></div>
          </>)}
          <div><Label>Adults</Label><Input type="number" min="1" value={f.adults} onChange={set("adults")} className={inp} /></div>
          <div><Label>Children</Label><Input type="number" min="0" value={f.children} onChange={set("children")} className={inp} /></div>
          <div className="col-span-2"><Label>Guest name</Label><Input value={f.guest_name} onChange={set("guest_name")} className={inp} /></div>
          <div><Label>Email</Label><Input type="email" value={f.guest_email} onChange={set("guest_email")} className={inp} /></div>
          <div><Label>Phone</Label><Input value={f.guest_phone} onChange={set("guest_phone")} className={inp} /></div>
          <div className="col-span-2"><Label>Rate type</Label>
            <select value={f.refund_type} onChange={set("refund_type")} className="mt-1 w-full bg-[#0f0f0f] border border-white/10 h-10 px-2"><option value="REFUNDABLE">Refundable</option><option value="NON_REFUNDABLE">Non-refundable</option></select></div>
          <div className="col-span-2"><Label>Notes</Label><Input value={f.special_requests} onChange={set("special_requests")} className={inp} /></div>
        </div>
        <p className="text-xs text-white/40">Price is calculated by the server. Availability is checked again at the moment of booking.</p>
        {err && <div role="alert" className="text-sm text-red-300 border border-red-500/30 p-3">{err}</div>}
        <button disabled={busy} onClick={submit} className="btn-gold w-full disabled:opacity-40">{busy ? "Creating…" : "Create booking"}</button>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminReservations({ property }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [sp, setSp] = useSearchParams();
  const [filters, setFilters] = useState({ q: "", status: sp.get("status") || "", from: "", to: "", payment_status: "" });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(Number(sp.get("open")) || null);
  const [creating, setCreating] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setFilters((f) => ({ ...f, q })); setPage(1); }, 350); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 15, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      const { data } = await api.get("/admin/bookings", { params });
      setRows(data.results); setMeta(data);
    } catch (e) { toast.error(errorMessage(e, "Couldn't load bookings")); }
    setLoading(false);
  }, [page, filters]);
  useEffect(() => { load(); }, [load]);

  const setF = (k) => (e) => { setFilters({ ...filters, [k]: e.target.value }); setPage(1); };
  const sel = "bg-[#0f0f0f] border border-white/10 h-10 px-2 text-sm text-white";

  return (
    <div data-testid="admin-reservations">
      <div className="flex flex-wrap gap-3 items-end mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, name, email, phone" className="pl-9 bg-[#0f0f0f] border-white/10 h-10" aria-label="Search bookings" data-testid="admin-search" />
        </div>
        <select value={filters.status} onChange={setF("status")} className={sel} aria-label="Filter by status"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</select>
        <select value={filters.payment_status} onChange={setF("payment_status")} className={sel} aria-label="Filter by payment"><option value="">Any payment</option>{Object.entries(PAYMENT_LABEL).filter(([k]) => k !== "unpaid").map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <div><Label className="text-[10px] uppercase text-white/40">From</Label><input type="date" value={filters.from} onChange={setF("from")} className={`${sel} block`} aria-label="From date" /></div>
        <div><Label className="text-[10px] uppercase text-white/40">To</Label><input type="date" value={filters.to} onChange={setF("to")} className={`${sel} block`} aria-label="To date" /></div>
        {(filters.status || filters.from || filters.to || filters.payment_status || q) && <button onClick={() => { setFilters({ q: "", status: "", from: "", to: "", payment_status: "" }); setQ(""); setPage(1); }} className="text-xs text-gold flex items-center gap-1"><X size={12} /> Clear</button>}
        <button onClick={() => setCreating(true)} className="btn-gold !py-2 !px-4 flex items-center gap-2" data-testid="admin-new-booking"><Plus size={14} /> New booking</button>
      </div>

      <div className="space-y-3" aria-busy={loading}>
        {loading && rows.length === 0 ? [...Array(4)].map((_, i) => <div key={i} className="glass h-20 animate-pulse" />)
          : rows.length === 0 ? <div className="text-center text-white/40 py-12">No bookings match these filters</div>
            : rows.map((b) => (
              <button key={b.id} onClick={() => setOpenId(b.id)} className="glass p-4 w-full text-left grid grid-cols-2 lg:grid-cols-[1.2fr_1.4fr_1fr_1fr_auto] gap-3 items-center hover:border-gold/30 border border-transparent transition-colors" data-testid={`admin-booking-${b.id}`}>
                <div><div className="text-white font-display">{b.reference}</div><div className="text-xs text-white/40">{b.source}</div></div>
                <div><div className="text-white text-sm">{b.guest_name || b.user_name}</div><div className="text-xs text-white/40 truncate">{b.guest_email}</div></div>
                <div className="text-sm text-white/70">{fmtDay(b.check_in_date, { day: "numeric", month: "short" })} → {fmtDay(b.check_out_date, { day: "numeric", month: "short" })}<div className="text-xs text-white/40">{b.booking_type === "overnight" ? `${b.number_of_nights} nights` : "hourly"}</div></div>
                <div><div className="text-gold font-display">{formatINR(b.total_amount)}</div><div className="text-xs text-white/40">{PAYMENT_LABEL[b.payment_status]}</div></div>
                <Badge status={b.status} />
              </button>
            ))}
      </div>

      <div className="flex items-center justify-between mt-6 text-sm text-white/50">
        <span>{meta.total} booking{meta.total !== 1 ? "s" : ""}</span>
        <div className="flex items-center gap-3">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-30" aria-label="Previous page"><ChevronLeft size={18} /></button>
          <span>Page {meta.page} of {meta.pages}</span>
          <button disabled={page >= meta.pages} onClick={() => setPage(page + 1)} className="disabled:opacity-30" aria-label="Next page"><ChevronRight size={18} /></button>
        </div>
      </div>

      {openId && <BookingDetail id={openId} onClose={() => { setOpenId(null); if (sp.get("open")) { sp.delete("open"); setSp(sp, { replace: true }); } }} onChanged={load} />}
      {creating && property && <ManualBooking property={property} onClose={() => setCreating(false)} onCreated={load} />}
    </div>
  );
}
